import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Search, Star, Loader2, Plus, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

/**
 * Standardized medical-condition picker.
 *
 * Props:
 *   value      → selected condition object (or null)
 *   onChange   → (conditionObj|null) — full object with slug, name, category, warnings…
 *                For custom conditions we emit { name, is_custom: true } (no slug)
 */
export default function MedicalConditionPicker({ value, onChange, autoFocus = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [customName, setCustomName] = useState("");

  // Fetch on open + on debounced query change
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/medical-conditions", {
          params: { q, limit: 40 },
        });
        if (!cancelled) setResults(data.results || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  const highlight = (name) => {
    if (!q) return name;
    const idx = name.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return name;
    return (
      <>
        {name.slice(0, idx)}
        <b className="text-ink">{name.slice(idx, idx + q.length)}</b>
        {name.slice(idx + q.length)}
      </>
    );
  };

  const pick = (c) => {
    onChange(c);
    setOpen(false);
    setQ("");
    setCustomMode(false);
  };

  const commitCustom = () => {
    const trimmed = customName.trim();
    if (!trimmed) return;
    onChange({
      slug: null,
      name: trimmed,
      condition: trimmed,
      category: null,
      category_label: "Custom",
      category_icon: "•",
      warnings: [],
      is_custom: true,
    });
    setOpen(false);
    setCustomMode(false);
    setCustomName("");
    setQ("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="mh-condition-picker-trigger"
          className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm text-left bg-white hover:border-pine/40 transition flex items-center justify-between gap-2"
        >
          {value ? (
            <span className="flex items-center gap-2 flex-1 min-w-0 truncate">
              <span className="text-base leading-none">{value.category_icon || "•"}</span>
              <span className="font-medium text-ink truncate">{value.name || value.condition}</span>
              {value.category_label && (
                <Badge variant="outline" className="text-[10px] shrink-0 border-pine/20 text-ink-mid">
                  {value.category_label}
                </Badge>
              )}
            </span>
          ) : (
            <span className="text-ink-soft flex items-center gap-2">
              <Search className="w-4 h-4" /> Search medical condition…
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[440px] p-0" data-testid="mh-condition-picker">
        {!customMode && (
          <>
            <div className="p-3 border-b border-[#E8EAE9]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
                <Input
                  autoFocus={autoFocus || open}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Type 2 letters to search hundreds of conditions…"
                  className="pl-9"
                  data-testid="mh-condition-search-input"
                />
              </div>
            </div>
            <div className="max-h-[360px] overflow-y-auto">
              {loading && (
                <div className="flex items-center justify-center py-8 text-ink-soft">
                  <Loader2 className="w-4 h-4 animate-spin" />
                </div>
              )}
              {!loading && results.length === 0 && (
                <div className="text-center py-8 px-4">
                  <div className="text-sm text-ink-soft">Condition not found</div>
                  <button
                    type="button"
                    onClick={() => { setCustomMode(true); setCustomName(q); }}
                    data-testid="mh-add-custom-btn"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-pine text-linen text-xs hover:bg-pine/90"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Custom Condition
                  </button>
                </div>
              )}
              {!loading && !q && results.some((c) => c.popular) && (
                <div className="px-3 pt-3 pb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-ink-soft">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Popular
                </div>
              )}
              {!loading && results.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => pick(c)}
                  data-testid={`mh-condition-result-${c.slug}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-linen border-b border-[#F2F0EB] last:border-b-0 transition flex items-center gap-3"
                >
                  <span className="text-lg leading-none">{c.category_icon || "•"}</span>
                  <span className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-ink flex items-center gap-1.5">
                      {highlight(c.name)}
                      {c.popular && !q && (
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />
                      )}
                    </span>
                    <span className="text-[11px] text-ink-soft uppercase tracking-wider">
                      {c.category_label}
                    </span>
                  </span>
                  {c.warnings?.length > 0 && (
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" title="Has clinical alerts" />
                  )}
                </button>
              ))}
              {!loading && q && results.length > 0 && (
                <div className="p-3 border-t border-[#E8EAE9] text-center">
                  <button
                    type="button"
                    onClick={() => { setCustomMode(true); setCustomName(q); }}
                    data-testid="mh-add-custom-secondary-btn"
                    className="text-xs text-ink-mid hover:text-ink underline"
                  >
                    Not here? Add “{q}” as a custom condition
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {customMode && (
          <div className="p-4 space-y-3" data-testid="mh-condition-custom-form">
            <div className="text-sm font-medium text-ink">Add custom condition</div>
            <Input
              autoFocus
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Condition name"
              data-testid="mh-condition-custom-input"
              onKeyDown={(e) => e.key === "Enter" && commitCustom()}
            />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => { setCustomMode(false); }}
                className="text-xs text-ink-mid hover:text-ink px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={commitCustom}
                disabled={!customName.trim()}
                data-testid="mh-condition-custom-save"
                className="text-xs px-4 py-1.5 rounded-full bg-pine text-linen disabled:opacity-50"
              >
                Add condition
              </button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
